/**
 * Rent roll report — `GET reports/rent-roll/`, manager roles only. `upcoming`/
 * `overdue` nest real `InvoiceListRow` rows (quantised decimal-string money);
 * `summary.total_upcoming`/`total_overdue` are plain JSON numbers — see the
 * per-field note in src/types/report.ts. No chart library in this project;
 * rendered as plain tables.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { getRentRoll, getRentRollCsv } from '../../api/endpoints/reports';
import type { InvoiceListRow } from '../../types/payment';

function InvoiceTable({ rows }: { rows: InvoiceListRow[] }): React.JSX.Element {
  const isMobile = useIsMobile();
  if (rows.length === 0) return <p>None.</p>;

  if (isMobile) {
    return (
      <ul className="record-cards">
        {rows.map((row) => (
          <li key={row.id} className="record-card">
            <div className="record-card__row">
              <span className="record-card__label">Invoice</span>
              <span className="record-card__value">{row.invoice_number}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Tenant</span>
              <span className="record-card__value">{row.tenant_name}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Unit</span>
              <span className="record-card__value">{row.unit_number}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Property</span>
              <span className="record-card__value">{row.property_name}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Charge type</span>
              <span className="record-card__value">{row.charge_type}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Amount due</span>
              <span className="record-card__value">{row.amount_due}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Amount paid</span>
              <span className="record-card__value">{row.amount_paid}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Due date</span>
              <span className="record-card__value">{row.due_date}</span>
            </div>
            <div className="record-card__row">
              <span className="record-card__label">Status</span>
              <span className="record-card__value">{row.status}</span>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Invoice</th>
          <th>Tenant</th>
          <th>Unit</th>
          <th>Property</th>
          <th>Charge type</th>
          <th>Amount due</th>
          <th>Amount paid</th>
          <th>Due date</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{row.invoice_number}</td>
            <td>{row.tenant_name}</td>
            <td>{row.unit_number}</td>
            <td>{row.property_name}</td>
            <td>{row.charge_type}</td>
            <td>{row.amount_due}</td>
            <td>{row.amount_paid}</td>
            <td>{row.due_date}</td>
            <td>{row.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RentRollPage(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-rent-roll'],
    queryFn: () => getRentRoll(),
  });

  /** Object-URL download of the backend's `?format=csv` response. */
  const exportCsv = useMutation({
    mutationFn: async () => {
      const blob = await getRentRollCsv();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'rent-roll.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    },
  });

  /**
   * Client-side PDF, same shared chrome module `InvoiceDetailPage.tsx`'s
   * "Print invoice" uses. `getRentRoll()` (already loaded above) has
   * everything needed — no server-side PDF endpoint, matching the CSV
   * export's own pure-client precedent for `?format=` handling.
   */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const rentRoll = query.data;
      if (rentRoll === undefined) return;

      const { jsPDF } = await import('jspdf');
      const { createBrandedDocument, drawTable, downloadPdf, formatPdfMoney } = await import('../../lib/pdf');

      const { doc, contentStartY } = createBrandedDocument(jsPDF, 'RENT ROLL REPORT');
      let y = contentStartY;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`Total upcoming: ${formatPdfMoney(rentRoll.summary.total_upcoming)}`, 15, y);
      y += 6;
      doc.text(`Total overdue: ${formatPdfMoney(rentRoll.summary.total_overdue)}`, 15, y);
      y += 10;

      const head = ['Invoice', 'Tenant', 'Unit', 'Property', 'Amount due', 'Due date', 'Status'];
      const toRow = (row: InvoiceListRow): (string | number)[] => [
        row.invoice_number,
        row.tenant_name,
        row.unit_number,
        row.property_name,
        formatPdfMoney(row.amount_due),
        row.due_date,
        row.status,
      ];

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Upcoming', 15, y);
      y += 4;
      y = drawTable(doc, { startY: y, head, rows: rentRoll.upcoming.map(toRow) });
      y += 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Overdue', 15, y);
      y += 4;
      drawTable(doc, { startY: y, head, rows: rentRoll.overdue.map(toRow) });

      downloadPdf(doc, 'rent-roll.pdf');
    },
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const rentRoll = query.data;

  return (
    <section>
      <h2>Rent roll</h2>

      <p>
        <button type="button" disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
          {exportCsv.isPending ? 'Exporting…' : 'Export CSV'}
        </button>{' '}
        <button type="button" disabled={exportPdf.isPending} onClick={() => exportPdf.mutate()}>
          {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
        </button>
      </p>

      {exportCsv.isError && (
        <p role="alert" className="form__error">
          {parseApiError(exportCsv.error).message}
        </p>
      )}

      {exportPdf.isError && (
        <p role="alert" className="form__error">
          {parseApiError(exportPdf.error).message}
        </p>
      )}

      <dl>
        <dt>Total upcoming</dt>
        <dd>{rentRoll.summary.total_upcoming}</dd>
        <dt>Total overdue</dt>
        <dd>{rentRoll.summary.total_overdue}</dd>
      </dl>

      <h3>Upcoming</h3>
      <InvoiceTable rows={rentRoll.upcoming} />

      <h3>Overdue</h3>
      <InvoiceTable rows={rentRoll.overdue} />
    </section>
  );
}

export default RentRollPage;
